import React, { useMemo, useState } from 'react';

import { Combobox } from 'components/Combobox';
import { useToastHelper } from 'hooks/useToastHelper';
import useQuery from 'hooks/useQuery';
import services from 'services/services';
import {
    DSCOUT_EXPRESS_SUBTYPE_LABELS,
    DSCOUT_MISSION_DELIMITER,
    DSCOUT_MISSION_TYPE_EXPRESS,
    DSCOUT_MISSION_TYPE_LABELS
} from 'utils/constants';

/**
 * Customer-visible label for a Dscout study type key, falling back to the raw
 * key so an unmapped type stays selectable instead of rendering "undefined".
 *
 * `express` is an umbrella covering several study types, so it resolves through the
 * study's own `scooterType` instead. Dscout leaves that null on pre-v2 Express studies
 * and may add sub-types we do not know yet, so both cases fall back to `Express`.
 *
 * Naming note: "mission" in identifiers here (and in the api) is Dscout's old wire
 * vocabulary for what is now called a study — mission = study. Only customer-visible
 * copy uses "study"; identifiers keep "mission" to match the API contract.
 *
 * @param {string} missionType Raw type key from the API (e.g. `live`).
 * @param {Object} mission Study as returned by the API, source of `scooterType`.
 * @returns {string} Label to render.
 */
const missionTypeLabel = (missionType, mission) => {
    const typeLabel = DSCOUT_MISSION_TYPE_LABELS[missionType] || missionType;

    if (missionType !== DSCOUT_MISSION_TYPE_EXPRESS) {
        return typeLabel;
    }

    return DSCOUT_EXPRESS_SUBTYPE_LABELS[mission?.scooterType] || typeLabel;
};

/**
 * Searchable dropdown for selecting a Dscout study.
 * Studies without a splash URL are shown as disabled with a note.
 *
 * @param {Function} onChange Callback function triggered when the selected option changes.
 * @param {string|null} selectedMissionId Composite ID of already-linked study ({type}:{id}), or null.
 */
export function DscoutMissionSelect({ onChange, selectedMissionId }) {
    const { addErrorToast } = useToastHelper();
    const [value, setValue] = useState(selectedMissionId);

    const { data: missions, isLoading } = useQuery({
        queryFn: () => {
            return selectedMissionId ? services.getDscoutMission(selectedMissionId) : services.getDscoutMissions();
        },
        onSuccess: response => {
            if (selectedMissionId) {
                const [type] = selectedMissionId.split(DSCOUT_MISSION_DELIMITER);
                onChange({ ...response.mission, type });
            }
        },
        onError: (_, errorText) => {
            addErrorToast(errorText);
        }
    });

    const { items, missionsMap } = useMemo(() => {
        if (!missions) return { items: [], missionsMap: {} };

        if (selectedMissionId) {
            const [type] = selectedMissionId.split(DSCOUT_MISSION_DELIMITER);
            const mission = missions.mission;
            const projectName = mission.project ? mission.project.name : 'Unknown project';
            return {
                items: [
                    {
                        value: selectedMissionId,
                        label: `[${projectName}] ${mission.name} (${missionTypeLabel(type, mission)})`
                    }
                ],
                missionsMap: { [selectedMissionId]: { ...mission, type } }
            };
        }

        const items = [];
        const map = {};

        Object.keys(missions).forEach(missionType => {
            missions[missionType].forEach(mission => {
                const projectName = mission.project ? mission.project.name : 'Unknown project';
                const compositeValue = `${missionType}${DSCOUT_MISSION_DELIMITER}${mission.id}`;
                const hasSplashUrl = !!mission.splashUrl;

                items.push({
                    value: compositeValue,
                    label: `[${projectName}] ${mission.name} (${missionTypeLabel(missionType, mission)})`,
                    disabled: !hasSplashUrl,
                    description: !hasSplashUrl ? 'Enable splash page in Dscout to use this study' : undefined
                });

                map[compositeValue] = { ...mission, type: missionType };
            });
        });

        return { items, missionsMap: map };
    }, [missions, selectedMissionId]);

    const handleChange = compositeValue => {
        setValue(compositeValue);
        const mission = missionsMap[compositeValue];
        onChange(mission || null);
    };

    if (isLoading) {
        return <div className="px-2 py-1">Loading studies...</div>;
    }

    return <Combobox items={items} value={value} onChange={handleChange} ariaLabel="Select a Dscout study" />;
}

export default DscoutMissionSelect;
